import {
  ArrowBack,
  InfoOutlined,
  LocalHospitalRounded,
  VisibilityRounded,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import MainLayout from "../../layouts/main/main_layout";
import Loading from "../../components/loading/loading";
import RequiredMark from "../../components/required_mark/required_mark";
import YesOrNoChips from "../../components/yes_or_no_chips/yes_or_no_chips";
import { useFeedback } from "../../providers/feedback/feeedback";
import { queryClient } from "../../providers/query/query";
import { getDistrictMasterData, getStateMasterData } from "../../services/master/master_service";
import {
  createHospital,
  getHospital,
  updateHospital,
} from "../../services/hospitals/hospitals";
import { hospitals } from "../../routes/routes_path";
import locales from "../../constants/locales";
import { Hospital, HospitalType, MasterOption } from "./model/hospital";
import { black, greyEnabled, orange, orangeLightActive, purple, purpleLight } from "../../theme/colors";
import { DEFAULT_ORG_ID } from "../../constants";
import useUser from "../../providers/user/user_provider";
import { useSelectedOrgStore } from "../../store/use-selected-org-store";
import OrgSelector from "../../components/org_selector/org_selector";

const dropdownPaperProps = {
  elevation: 0,
  sx: {
    mt: 0.5,
    borderRadius: 2,
    border: "1px solid #ECECEC",
    boxShadow: "0px 12px 28px -8px rgba(27, 0, 27, 0.18)",
  },
};

const HOSPITAL_TYPE_OPTIONS: { value: HospitalType; label: string }[] = [
  { value: "government", label: "Government" },
  { value: "private", label: "Private" },
  { value: "trust", label: "Trust" },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Google Maps links encode the coordinates in a couple of well-known
// formats depending on how they were shared (map view vs a dropped pin's
// "Share" link). Shortened goo.gl/maps links can't be resolved client-side
// (they require following a redirect), so admins using those need to switch
// to manual lat/long entry instead.
function parseLatLngFromGoogleMapsUrl(url: string): { lat: number; lon: number } | null {
  const atMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (atMatch) return { lat: parseFloat(atMatch[1]), lon: parseFloat(atMatch[2]) };
  const qMatch = url.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (qMatch) return { lat: parseFloat(qMatch[1]), lon: parseFloat(qMatch[2]) };
  const llMatch = url.match(/[?&]ll=(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (llMatch) return { lat: parseFloat(llMatch[1]), lon: parseFloat(llMatch[2]) };
  return null;
}

interface HospitalFormInputs {
  name: string;
  lat: number | string;
  lon: number | string;
  googleMapLink: string;
  contactNo: string;
  email: string;
  address: string;
  state: MasterOption | null;
  district: MasterOption | null;
  isVerified: boolean;
  isActive: boolean;
  hospitalType: HospitalType | null;
}

const EMPTY_FORM: HospitalFormInputs = {
  name: "",
  lat: "",
  lon: "",
  googleMapLink: "",
  contactNo: "",
  email: "",
  address: "",
  state: null,
  district: null,
  isVerified: false,
  isActive: true,
  hospitalType: null,
};

export default function AddEditHospital({ viewMode = false }: { viewMode?: boolean }) {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { showSnackbar } = useFeedback();
  const { userPermissions } = useUser();
  const { selectedOrgId } = useSelectedOrgStore();
  // A Platform Admin picking an org below creates the hospital there;
  // otherwise falls back to the caller's own org, matching create_user.tsx.
  const targetOrgId = selectedOrgId ?? userPermissions?.orgId ?? DEFAULT_ORG_ID;
  const [submitting, setSubmitting] = useState(false);
  const [coordinateMode, setCoordinateMode] = useState<"manual" | "link">("manual");
  const [pendingDistrictId, setPendingDistrictId] = useState<string | null>(null);
  const [selectedLocale, setSelectedLocale] = useState("en");
  // Name/Address are entered fresh per language; everything else (location,
  // contact, type, verified status) is set once on the English row and
  // carried over to every translation — so it's only editable from there.
  const isSharedFieldsEditable = !isEdit || selectedLocale === "en";

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<HospitalFormInputs>({ defaultValues: EMPTY_FORM });

  const selectedState = watch("state");

  const { data: stateList, isLoading: isStatesLoading } = useQuery(
    ["state-master-list"],
    async () => {
      let response = await getStateMasterData();
      return (response?.data?.data?.list || []) as MasterOption[];
    }
  );

  const { data: districtList } = useQuery(
    ["district-master-list", selectedState?.id],
    async () => {
      let response = await getDistrictMasterData(selectedState!.id);
      return (response?.data?.data?.list || []) as MasterOption[];
    },
    { enabled: !!selectedState }
  );

  // Once the district list for the pre-filled state arrives, resolve the
  // hospital's stored districtId (a string) against it and select the match.
  useEffect(() => {
    if (pendingDistrictId && districtList) {
      const match = districtList.find((item) => String(item.id) === pendingDistrictId);
      if (match) setValue("district", match);
      setPendingDistrictId(null);
    }
  }, [pendingDistrictId, districtList, setValue]);

  // GET /v1/admin/hospitals/:id returns the full hospital (including its
  // schemes/specialities/facilities as {id,name} tags), so it's used directly
  // as the initial data source for edit/view instead of paging through the list.
  // Keyed on selectedLocale too — switching language tabs re-fetches that
  // language's row, falling back to the English row (with name/address
  // blanked) if this hospital has no content in that language yet.
  const { data: hospitalDetails, isLoading: isDetailsLoading } = useQuery(
    ["hospital-details", id, selectedLocale],
    async () => {
      let response = await getHospital(id!, selectedLocale);
      return response.data.data as Hospital;
    },
    { enabled: !!id }
  );
  const isTranslated = hospitalDetails?.isTranslated !== false;

  useEffect(() => {
    if (hospitalDetails && stateList) {
      const matchedState = stateList.find(
        (item) => String(item.id) === String(hospitalDetails.stateId)
      ) || null;
      reset({
        name: hospitalDetails.name,
        lat: hospitalDetails.lat,
        lon: hospitalDetails.lon,
        googleMapLink: "",
        contactNo: hospitalDetails.contactNo || "",
        email: hospitalDetails.email || "",
        address: hospitalDetails.address || "",
        state: matchedState,
        district: null,
        isVerified: hospitalDetails.isVerified,
        isActive: hospitalDetails.isActive !== false,
        hospitalType: hospitalDetails.hospitalType || null,
      });
      if (matchedState && hospitalDetails.districtId) {
        setPendingDistrictId(String(hospitalDetails.districtId));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalDetails, stateList, reset]);

  const handleCoordinateModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: "manual" | "link" | null
  ) => {
    if (!newMode) return;
    setCoordinateMode(newMode);
    clearErrors(["lat", "lon", "googleMapLink"]);
  };

  const onSubmit: SubmitHandler<HospitalFormInputs> = (data) => {
    const coords =
      coordinateMode === "link"
        ? parseLatLngFromGoogleMapsUrl(data.googleMapLink)!
        : { lat: +data.lat, lon: +data.lon };

    setSubmitting(true);
    const payload = {
      name: data.name,
      lat: coords.lat,
      lon: coords.lon,
      contact_no: data.contactNo,
      email: data.email || null,
      address: data.address || null,
      state_id: data.state ? String(data.state.id) : null,
      district_id: data.district ? String(data.district.id) : null,
      is_verified: data.isVerified,
      hospital_type: data.hospitalType || null,
      // Hospital Schemes/Specialities/Facilities aren't editable from this
      // form (per product decision) — always send a fixed scheme and clear
      // specialities/facilities rather than exposing pickers for them.
      hospital_scheme_ids: [1],
      hospital_speciality_ids: [],
      hospital_facility_ids: [],
      orgId: targetOrgId,
    };
    const request = isEdit
      // is_active is a per-language status (only meaningful once a language
      // row exists to toggle) — Add Hospital's POST schema doesn't accept it.
      ? updateHospital(id!, { ...payload, is_active: data.isActive }, selectedLocale)
      : createHospital(payload);
    request
      .then(() => {
        showSnackbar(isEdit ? "Hospital updated successfully" : "Hospital added successfully");
        queryClient.invalidateQueries({ queryKey: ["hospitals"] });
        if (isEdit) {
          // Stay on the page — the admin may want to switch to another
          // language tab next, same as editing a scheme's per-language content.
          queryClient.invalidateQueries({ queryKey: ["hospital-details", id] });
        } else {
          navigate(hospitals);
        }
      })
      .catch((e) => {
        showSnackbar(
          e?.response?.data?.error?.message ||
            `Something went wrong while ${isEdit ? "updating" : "adding"} the hospital.`
        );
      })
      .finally(() => setSubmitting(false));
  };

  const isPageLoading = isStatesLoading || (isEdit && isDetailsLoading);

  const nameField = register("name", { required: "Please enter the hospital name" });
  const contactNoField = register("contactNo", {
    required: "Please enter a contact number",
  });

  return (
    <MainLayout>
      <Box>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: purpleLight,
                flexShrink: 0,
              }}
            >
              {viewMode ? (
                <VisibilityRounded sx={{ color: purple, fontSize: 22 }} />
              ) : (
                <LocalHospitalRounded sx={{ color: purple, fontSize: 22 }} />
              )}
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} color={black}>
                {viewMode ? "View Hospital" : isEdit ? "Edit Hospital" : "Add Hospital"}
              </Typography>
              <Typography variant="body2" color={greyEnabled}>
                {viewMode
                  ? "Review this hospital's details."
                  : "Fill in the hospital's details below."}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Card
          elevation={0}
          sx={{ width: "100%", mt: 2, borderRadius: 3, border: "1px solid #ECECEC" }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Box mb={2} maxWidth={360}>
              {isEdit || viewMode ? (
                <TextField
                  value={hospitalDetails?.orgName || ""}
                  fullWidth
                  label="Organisation"
                  disabled
                />
              ) : (
                <OrgSelector label="Organisation" fullWidth lockToOwnOrg platformDefaultsToOwnOrg />
              )}
            </Box>
            {isEdit && (
              <>
                <Tabs
                  value={locales.findIndex((locale) => locale.code === selectedLocale)}
                  onChange={(_, index: number) => {
                    if (submitting) return;
                    setSelectedLocale(locales[index].code);
                  }}
                  TabIndicatorProps={{
                    style: { backgroundColor: purple, height: 3, borderRadius: 3 },
                  }}
                  sx={{ mb: 2 }}
                >
                  {locales.map((locale) => (
                    <Tab
                      key={locale.code}
                      label={locale.name}
                      sx={{
                        textTransform: "capitalize",
                        maxWidth: 50,
                        color: greyEnabled,
                        "&.Mui-selected": { color: purple },
                      }}
                    />
                  ))}
                </Tabs>
                <Divider sx={{ mb: 2 }} />

                {!isDetailsLoading && !isTranslated && (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{
                      mb: 2,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: orangeLightActive,
                      border: `1px solid ${orangeLightActive}`,
                    }}
                  >
                    <InfoOutlined sx={{ color: orange, fontSize: 20 }} />
                    <Typography variant="body2" color="#1B001B">
                      This hospital has no content in{" "}
                      {locales.find((locale) => locale.code === selectedLocale)?.name ||
                        selectedLocale}{" "}
                      yet — fill in the Name and Address below to add this translation. Other
                      details are shared across languages and can only be changed from the
                      English tab.
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {isPageLoading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <Loading />
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid
                  container
                  spacing={3}
                  sx={{
                    "& .MuiInputLabel-root": { fontSize: "1.05rem" },
                    "& .MuiInputLabel-shrink": { fontSize: "0.95rem" },
                  }}
                >
                  <Grid item md={6} sm={12}>
                    <TextField
                      fullWidth
                      label={<>Hospital Name<RequiredMark /></>}
                      placeholder="Enter hospital name"
                      disabled={viewMode}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      {...nameField}
                      onChange={(e) => {
                        // Strip punctuation/symbols but keep letters from any
                        // script (Telugu, Hindi, etc.) and digits — this field
                        // is filled in per-language, so it can't be Latin-only.
                        e.target.value = e.target.value.replace(/[^\p{L}\p{N}\s]/gu, "");
                        nameField.onChange(e);
                      }}
                    />
                  </Grid>
                  <Grid item md={6} sm={12}>
                    <TextField
                      fullWidth
                      label={<>Contact Number<RequiredMark /></>}
                      placeholder="Enter contact number"
                      disabled={viewMode || !isSharedFieldsEditable}
                      error={!!errors.contactNo}
                      helperText={errors.contactNo?.message}
                      inputProps={{ inputMode: "numeric" }}
                      {...contactNoField}
                      onChange={(e) => {
                        e.target.value = e.target.value.replace(/[^0-9]/g, "");
                        contactNoField.onChange(e);
                      }}
                    />
                  </Grid>

                  <Grid item md={12}>
                    <TextField
                      fullWidth
                      type="email"
                      label="Email (Optional)"
                      placeholder="Enter email address"
                      disabled={viewMode || !isSharedFieldsEditable}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      {...register("email", {
                        pattern: { value: EMAIL_PATTERN, message: "Enter a valid email address" },
                      })}
                    />
                  </Grid>

                  <Grid item md={6} sm={12}>
                    <Controller
                      name="state"
                      control={control}
                      rules={{ validate: (value) => !!value || "Please select a state" }}
                      render={({ field: { onChange, value } }) => (
                        <Autocomplete
                          fullWidth
                          disabled={viewMode || !isSharedFieldsEditable}
                          options={stateList || []}
                          value={value}
                          getOptionLabel={(option) => option.value}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          componentsProps={{ paper: dropdownPaperProps }}
                          onChange={(_, selected) => {
                            onChange(selected);
                            setValue("district", null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={<>State<RequiredMark /></>}
                              placeholder="Select state"
                              error={!!errors.state}
                              helperText={errors.state?.message}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={6} sm={12}>
                    <Controller
                      name="district"
                      control={control}
                      rules={{ validate: (value) => !!value || "Please select a district" }}
                      render={({ field: { onChange, value } }) => (
                        <Autocomplete
                          fullWidth
                          disabled={viewMode || !isSharedFieldsEditable || !selectedState}
                          options={districtList || []}
                          value={value}
                          getOptionLabel={(option) => option.value}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          componentsProps={{ paper: dropdownPaperProps }}
                          onChange={(_, selected) => onChange(selected)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={<>District<RequiredMark /></>}
                              placeholder={selectedState ? "Select district" : "Select a state first"}
                              error={!!errors.district}
                              helperText={errors.district?.message}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item md={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label={<>Address<RequiredMark /></>}
                      placeholder="Enter address"
                      disabled={viewMode}
                      error={!!errors.address}
                      helperText={errors.address?.message}
                      {...register("address", { required: "Please enter the address" })}
                    />
                  </Grid>

                  <Grid item md={12}>
                    <Typography variant="body1" fontWeight={600} color={black} fontSize="1.1rem" mb={0.5}>
                      Hospital Type (Optional)
                    </Typography>
                    <Controller
                      name="hospitalType"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {HOSPITAL_TYPE_OPTIONS.map((option) => (
                            <Chip
                              key={option.value}
                              label={option.label}
                              clickable={!viewMode && isSharedFieldsEditable}
                              disabled={viewMode || !isSharedFieldsEditable}
                              variant={value === option.value ? "filled" : "outlined"}
                              color="primary"
                              onClick={() =>
                                onChange(value === option.value ? null : option.value)
                              }
                            />
                          ))}
                        </Box>
                      )}
                    />
                  </Grid>

                  <Grid item md={12}>
                    <Typography variant="body1" fontWeight={600} color={black} fontSize="1.1rem" mb={1}>
                      Location<RequiredMark />
                    </Typography>
                    {!viewMode && isSharedFieldsEditable && (
                      <ToggleButtonGroup
                        value={coordinateMode}
                        exclusive
                        onChange={handleCoordinateModeChange}
                        size="small"
                        sx={{
                          mb: 2,
                          "& .MuiToggleButton-root": {
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            borderColor: "#ABA0AB",
                            color: greyEnabled,
                            "&.Mui-selected": {
                              bgcolor: purpleLight,
                              color: purple,
                              "&:hover": { bgcolor: purpleLight },
                            },
                          },
                        }}
                      >
                        <ToggleButton value="manual">Enter Coordinates</ToggleButton>
                        <ToggleButton value="link">Paste Google Maps Link</ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  </Grid>

                  {coordinateMode === "manual" || viewMode ? (
                    <>
                      <Grid item md={6} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label={<>Latitude<RequiredMark /></>}
                          placeholder="Enter latitude"
                          disabled={viewMode || !isSharedFieldsEditable}
                          error={!!errors.lat}
                          helperText={errors.lat?.message}
                          inputProps={{ min: 0, step: "any" }}
                          {...register("lat", {
                            validate: (value) => {
                              if (coordinateMode !== "manual") return true;
                              if (value === "" || value === null || value === undefined)
                                return "Please enter latitude";
                              if (+value < 0) return "Latitude cannot be negative";
                              return true;
                            },
                          })}
                        />
                      </Grid>
                      <Grid item md={6} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label={<>Longitude<RequiredMark /></>}
                          placeholder="Enter longitude"
                          disabled={viewMode || !isSharedFieldsEditable}
                          error={!!errors.lon}
                          helperText={errors.lon?.message}
                          inputProps={{ min: 0, step: "any" }}
                          {...register("lon", {
                            validate: (value) => {
                              if (coordinateMode !== "manual") return true;
                              if (value === "" || value === null || value === undefined)
                                return "Please enter longitude";
                              if (+value < 0) return "Longitude cannot be negative";
                              return true;
                            },
                          })}
                        />
                      </Grid>
                    </>
                  ) : (
                    <Grid item md={12}>
                      <TextField
                        fullWidth
                        label="Google Maps Link"
                        placeholder="Paste a Google Maps link that includes the location"
                        error={!!errors.googleMapLink}
                        helperText={
                          errors.googleMapLink?.message ||
                          "Use a link with visible coordinates in it (e.g. from a dropped pin's Share link) — shortened goo.gl links aren't supported."
                        }
                        {...register("googleMapLink", {
                          validate: (value) => {
                            if (coordinateMode !== "link") return true;
                            if (!value) return "Please enter a Google Maps link";
                            return (
                              !!parseLatLngFromGoogleMapsUrl(value) ||
                              "Could not find coordinates in this link. Try a link that shows the location's coordinates, or switch to manual entry."
                            );
                          },
                        })}
                      />
                    </Grid>
                  )}

                  <Grid item md={12}>
                    <Typography variant="body1" fontWeight={600} color={black} fontSize="1.1rem" mb={0.5}>
                      Is Verified
                    </Typography>
                    <Controller
                      name="isVerified"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <YesOrNoChips
                          value={value}
                          onChange={(val) => onChange(!!val)}
                          error={false}
                          helperText={undefined}
                          disabled={viewMode || !isSharedFieldsEditable}
                        />
                      )}
                    />
                  </Grid>

                  {isEdit && (
                    <Grid item md={12}>
                      <Typography variant="body1" fontWeight={600} color={black} fontSize="1.1rem" mb={0.5}>
                        Is Active ({locales.find((locale) => locale.code === selectedLocale)?.name || selectedLocale})
                      </Typography>
                      <Controller
                        name="isActive"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <YesOrNoChips
                            value={value}
                            onChange={(val) => onChange(!!val)}
                            error={false}
                            helperText={undefined}
                            disabled={viewMode}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>

                <Box mt={2} mb={2}>
                  <Divider />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Button
                    variant="outlined"
                    disabled={submitting}
                    onClick={() => navigate(-1)}
                    sx={{
                      minWidth: 150,
                      borderColor: purple,
                      color: purple,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { borderColor: purple, bgcolor: purpleLight },
                    }}
                  >
                    {viewMode ? "Back" : "Cancel"}
                  </Button>
                  {!viewMode && (
                    <Button
                      type="submit"
                      variant="contained"
                      disableElevation
                      disabled={submitting}
                      startIcon={submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
                      sx={{
                        minWidth: 150,
                        bgcolor: purple,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": { bgcolor: "#7A2074" },
                      }}
                    >
                      {isEdit ? "Update Hospital" : "Add Hospital"}
                    </Button>
                  )}
                </Box>
              </form>
            )}
          </CardContent>
        </Card>
      </Box>
    </MainLayout>
  );
}
